<template>
    <div>
        <component
            :is="componentName"
            :guide="guide"
            :display-name="displayName"
            :historic-import="historicImport"
            :automatic-import="automaticImport"
            :timeframe="timeframe"
            :saved-view-created="savedViewCreated"
            :successful-percentage="successfulPercentage"
            :failed-percentage="failedPercentage"
            :failed-items="failedItems"
            :successful-stat="successfulStat"
            :failed-stat="failedStat"
            :review-info-text="reviewInfoText"
            :total-items="totalItems"
            :type="type"
            @generate-csv-errors="generateCSVErrors"
            @view-insights="$emit('view-insights')"
            @view-insights-new-tab="$emit('view-insights-new-tab')" />
    </div>
</template>

<script>
import { PendoCard, PendoButton, PendoDivider, PendoTooltip } from '@pendo/components';
import { pluralize } from '@/utils/formatters';
import { mapActions, mapGetters } from 'vuex';
import { formatProductAreas } from '@/voc/helpers/common.helper';
import { IMPORTANCES } from '@/voc/constants/constants';
import ImportFeedbackDrawerGuideReviewStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-guide-review-step/import-feedback-drawer-guide-review-step.vue';
import ImportFeedbackDrawerCsvReviewStep from '@/voc/components/drawers/import-feedback-drawer/steps/import-feedback-drawer-csv-review-step/import-feedback-drawer-csv-review-step.vue';
import moment from '@utils/moment-wrapper';

export default {
    components: {
        PendoCard,
        PendoButton,
        PendoDivider,
        ImportFeedbackDrawerGuideReviewStep,
        ImportFeedbackDrawerCsvReviewStep,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        completedImport: {
            type: Object || null,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        historicImport: {
            type: Boolean,
            default: true,
        },
        automaticImport: {
            type: Boolean,
            default: true,
        },
        guide: {
            type: [Object, null],
            default: () => null,
        },
        timeframe: {
            type: [Object, null],
            default: () => null,
        },
        reviewInfoText: {
            type: String,
            default: '',
        },
    },
    emits: ['view-insights', 'view-insights-new-tab'],
    computed: {
        ...mapGetters({
            apps: 'apps/list',
            filterChoices: 'vocItems/filters/choices',
        }),
        totalItems () {
            return this.completedImport?.totalItemCount || 0;
        },
        uploadedItems () {
            return this.completedImport?.countSavedToDatastore || 0;
        },
        failedItems () {
            return this.completedImport?.errors?.length || 0;
        },
        successfulPercentage () {
            return Math.round((this.uploadedItems / this.totalItems) * 100) || 0;
        },
        failedPercentage () {
            return Math.round((this.failedItems / this.totalItems) * 100) || 0;
        },
        successfulStat () {
            return `${this.uploadedItems} out of ${this.totalItems} ${pluralize('item', this.totalItems)}`;
        },
        failedStat () {
            return `${this.failedItems} out of ${this.totalItems} ${pluralize('item', this.totalItems)}`;
        },
        savedViewCreated () {
            return !!this.completedImport?.savedViewId;
        },
        isGuideOrNPS () {
            return this.type === 'NPS' || this.type === 'guide';
        },
        componentName () {
            return this.isGuideOrNPS ? 'ImportFeedbackDrawerGuideReviewStep' : 'ImportFeedbackDrawerCsvReviewStep';
        },
        displayName () {
            return this.completedImport?.displayName || '';
        },
    },
    created () {
        Promise.all([this.loadGroups(), this.loadChoices({ type: 'feedback' })]);
    },
    methods: {
        ...mapActions({
            loadGroups: 'groups/loadAll',
            loadChoices: 'vocItems/filters/loadChoices',
        }),
        formatProductAreas (productAreaIds) {
            const productArea = formatProductAreas(this.$store, productAreaIds)[0] || { name: '' };

            return productArea.name;
        },
        formatApp (appId) {
            const app = this.apps.find((app) => app.id === appId);

            return app ? app.name : '';
        },
        formatImportance (voteStrength) {
            return IMPORTANCES.find((importance) => importance.id === voteStrength).label;
        },
        formatCreatedBy (createdBy) {
            const createdByUsersList = this.filterChoices.createdByUsers;
            const createdByString = String(createdBy);

            return createdByUsersList.find((user) => user.id === createdByString)?.label || '';
        },
        formatCreatedAt (createdAt) {
            return moment(new Date(createdAt * 1000)).format('YYYY-MM-DD');
        },
        formatLabels (labels) {
            return labels
                .map((label) => {
                    if (label.category !== '') {
                        return `${label.category}: ${label.name}`;
                    }

                    return label.name;
                })
                .join(', ');
        },
        formatLinks (links) {
            return links.map((link) => link.linkUrl).join(', ');
        },
        formatStringForCsv (stringForCsv) {
            stringForCsv = stringForCsv.replace(/"/g, '""');

            return `"${stringForCsv}"`;
        },
        generateCSVErrors () {
            const csvData = [
                [
                    'Error message',
                    'Visitor',
                    'Account',
                    'Title',
                    'App',
                    'Product area',
                    'Description',
                    'Labels',
                    'Links',
                    'Importance',
                    'Submitter',
                    'Submitted date',
                ],
            ];

            this.completedImport.errors?.forEach((erroringRow) => {
                const errorMessageString = erroringRow.errors.map((error) => error.errorMessage).join(', ');
                const csvRow = [
                    this.formatStringForCsv(errorMessageString),
                    this.formatStringForCsv(erroringRow.row.onBehalfOf),
                    this.formatStringForCsv(erroringRow.row.accountId),
                    erroringRow.row.formDataContent[0]
                        ? this.formatStringForCsv(erroringRow.row.formDataContent[0])
                        : '',
                    this.formatStringForCsv(this.formatApp(erroringRow.row.appId)),
                    erroringRow.row.productAreaIds
                        ? this.formatStringForCsv(this.formatProductAreas(erroringRow.row.productAreaIds))
                        : '',
                    erroringRow.row.formDataContent[1]
                        ? this.formatStringForCsv(erroringRow.row.formDataContent[1])
                        : '',
                    erroringRow.row.labels
                        ? this.formatStringForCsv(`${this.formatLabels(erroringRow.row.labels)}`)
                        : '',
                    erroringRow.row.links ? this.formatStringForCsv(`${this.formatLinks(erroringRow.row.links)}`) : '',
                    this.formatImportance(erroringRow.row.voteStrength),
                    this.formatCreatedBy(erroringRow.row.createdBy),
                    this.formatCreatedAt(erroringRow.row.createdAt),
                ];
                csvData.push(csvRow);
            });

            const csvContent = csvData.map((row) => row.join(',')).join('\n');
            const csvFilename = 'Feedback-Import-Errors.csv';

            // Create a blob
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            if (navigator.msSaveBlob) {
                // IE 10+
                navigator.msSaveBlob(blob, csvFilename);
            } else {
                const url = URL.createObjectURL(blob);

                // Create a link to download it
                const downloadCsvLink = document.createElement('a');
                downloadCsvLink.setAttribute('href', url);
                downloadCsvLink.setAttribute('download', '[Listen]Feedback-Import-Errors.csv');
                downloadCsvLink.click();
            }
        },
    },
};
</script>
